// Users tab — paginated list of every klonit user with search + active filter
// + activate/deactivate switch + drill-down to detail.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, TextField, MenuItem, Table, TableHead, TableRow, TableCell,
    TableBody, TableContainer, Paper, Pagination, Chip, Switch, Tooltip, Alert,
    CircularProgress, Stack, IconButton, Snackbar,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDebounce } from '../utils/useDebounce';

import axiosInstance from '../utils/axiosInstance';
import { formatDate } from '../utils/format';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const PAGE_SIZE = 25;

const subscriptionChip = (sub) => {
    if (!sub) return <Chip size="small" label="No sub" sx={{ fontFamily }} />;
    const map = {
        active: 'success', trialing: 'info', freemium: 'default',
        past_due: 'error', canceled: 'default', incomplete: 'warning',
        requires_payment_method: 'warning',
    };
    const color = map[sub.status] || 'default';
    return <Chip size="small" color={color} label={sub.status} sx={{ fontFamily, fontWeight: 600 }} />;
};

const Users = () => {
    const navigate = useNavigate();

    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [togglingId, setTogglingId] = useState(null);
    const [snackbar, setSnackbar] = useState(null);

    const debouncedSearch = useDebounce(search, 350);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: PAGE_SIZE };
            if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
            if (status) params.status = status;
            const res = await axiosInstance.get('/admin/users', { params });
            setUsers(res.data?.users || []);
            setTotal(res.data?.total || 0);
            setPages(res.data?.pages || 1);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load users.');
        } finally {
            setLoading(false);
        }
    }, [page, debouncedSearch, status]);

    useEffect(() => { load(); }, [load]);

    const toggleActive = async (user) => {
        setTogglingId(user.user_id);
        try {
            const res = await axiosInstance.patch(`/admin/users/${user.user_id}/active`, {
                is_active: !user.is_active,
            });
            setUsers((prev) => prev.map(
                (u) => (u.user_id === user.user_id ? { ...u, is_active: !u.is_active } : u),
            ));
            setSnackbar({ severity: 'success', message: res.data?.message || 'User updated.' });
        } catch (err) {
            setSnackbar({
                severity: 'error',
                message: err.response?.data?.message || 'Could not update user.',
            });
        } finally {
            setTogglingId(null);
        }
    };

    return (
        <Box sx={{ fontFamily }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                   alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>
                        Users
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                        Suspend, restore, or drill into any klonit account.
                    </Typography>
                </Box>
                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>
                    {total} total
                </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                    size="small" label="Search email" value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    sx={{ minWidth: 260 }}
                />
                <TextField
                    select size="small" label="Status" value={status}
                    onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                    sx={{ minWidth: 160 }}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                </TextField>
            </Stack>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ fontFamily }}>{error}</Alert>
            ) : users.length === 0 ? (
                <Typography sx={{ fontFamily, color: '#64748b', py: 6, textAlign: 'center' }}>
                    No users match the filters.
                </Typography>
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
                        <Table aria-label="users">
                            <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>User ID</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Email</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Account</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Subscription</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }}>Period ends</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }} align="center">Active</TableCell>
                                    <TableCell sx={{ fontFamily, fontWeight: 700 }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((u) => (
                                    <TableRow key={u.user_id} hover>
                                        <TableCell sx={{ fontFamily, fontWeight: 600 }}>#{u.user_id}</TableCell>
                                        <TableCell sx={{ fontFamily }}>{u.user_email}</TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                            {u.account_type || '--'}
                                        </TableCell>
                                        <TableCell>{subscriptionChip(u.subscription)}</TableCell>
                                        <TableCell sx={{ fontFamily, color: '#64748b' }}>
                                            {formatDate(u.subscription?.current_period_end)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title={u.is_active ? 'Deactivate user' : 'Re-activate user'}>
                                                <span>
                                                    <Switch
                                                        size="small" checked={!!u.is_active}
                                                        disabled={togglingId === u.user_id}
                                                        onChange={() => toggleActive(u)}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="View detail">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => navigate(`/users/${u.user_id}`)}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {pages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={pages} page={page}
                                onChange={(_, p) => setPage(p)}
                                color="primary" size="small"
                            />
                        </Box>
                    )}
                </>
            )}

            <Snackbar
                open={!!snackbar} autoHideDuration={4500} onClose={() => setSnackbar(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {snackbar ? (
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ fontFamily }}>
                        {snackbar.message}
                    </Alert>
                ) : null}
            </Snackbar>
        </Box>
    );
};

export default Users;
